import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export type BrandRole = "OWNER" | "ADMIN" | "CREATOR" | "REVIEWER" | "SCHEDULER" | "VIEWER";

export interface BrandMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: BrandRole;
  lastActiveAt: string | null;
}

export function useMembers(brandId: string) {
  return useQuery({
    queryKey: ["members", brandId],
    queryFn: () => apiFetch<BrandMember[]>(`/brands/${brandId}/members`),
    enabled: !!brandId,
  });
}

export function useInviteMember(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: BrandRole }) =>
      apiFetch<{ inviteId: string }>(`/brands/${brandId}/invites`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", brandId] }),
  });
}

export function useUpdateMemberRole(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: BrandRole }) =>
      apiFetch(`/brands/${brandId}/members/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", brandId] }),
  });
}

export function useRemoveMember(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/brands/${brandId}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", brandId] }),
  });
}
